import { useState } from 'react'
import {
  CloseButton,
  Button,
  NativeSelect,
  HStack,
  Input,
  Text,
  VStack,
  Switch,
  Box,
  Spinner,
  Separator,
  Field,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useUnionSearch } from '../../hooks/useUnion'
import { useWorksiteSearch } from '../../hooks/useBusiness'
import { unionApi } from '../../lib/api'
import { ContactSearchSelect } from '../shared/ContactSearchSelect'

interface AffiliateUnionModalProps {
  isOpen: boolean
  onClose: () => void
  worksiteId?: string
  businessId?: string
}

export function AffiliateUnionModal({ isOpen, onClose, worksiteId, businessId }: AffiliateUnionModalProps) {
  const queryClient = useQueryClient()
  const { data: unionData } = useUnionSearch({ pageSize: 100 })

  const [unionId, setUnionId] = useState('')
  const [contractStartDate, setContractStartDate] = useState('')
  const [contractEndDate, setContractEndDate] = useState('')
  const [unionRepName, setUnionRepName] = useState('')
  const [employerContactId, setEmployerContactId] = useState<string | null>(null)
  const [employerContactName, setEmployerContactName] = useState<string | null>(null)
  const [excludedWorksiteIds, setExcludedWorksiteIds] = useState<Record<string, boolean>>({})

  const isBusiness = !!businessId

  // Fetch worksites for business to show coverage toggles
  const { data: worksiteData, isLoading: worksitesLoading } = useWorksiteSearch(
    { businessId: businessId!, pageSize: 200 },
    { enabled: isBusiness && isOpen }
  )

  const worksites = worksiteData?.items ?? []
  const hasWorksites = worksites.length > 0

  const toggleWorksiteExclusion = (wsId: string) => {
    setExcludedWorksiteIds(prev => ({ ...prev, [wsId]: !prev[wsId] }))
  }

  const mutation = useMutation({
    mutationFn: async () => {
      if (isBusiness) {
        const excluded = Object.entries(excludedWorksiteIds)
          .filter(([, isExcluded]) => isExcluded)
          .map(([id]) => id)
        await unionApi.affiliateBusiness(unionId, {
          businessId: businessId!,
          contractStartDate: contractStartDate || undefined,
          contractEndDate: contractEndDate || undefined,
          unionRepName: unionRepName || undefined,
          employerContactId: employerContactId || undefined,
          employerContactName: employerContactName || undefined,
          excludedWorksiteIds: excluded.length > 0 ? excluded : undefined,
        })
      } else {
        await unionApi.affiliateWorksite(unionId, {
          worksiteId: worksiteId!,
          contractStartDate: contractStartDate || undefined,
          contractEndDate: contractEndDate || undefined,
          unionRepName: unionRepName || undefined,
          employerContactId: employerContactId || undefined,
          employerContactName: employerContactName || undefined,
        })
      }
    },
    onSuccess: async () => {
      if (isBusiness) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['businesses', businessId] }),
          queryClient.invalidateQueries({ queryKey: ['worksites'] }),
          queryClient.invalidateQueries({ queryKey: ['unions'] }),
        ])
      } else {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['worksites', worksiteId] }),
          queryClient.invalidateQueries({ queryKey: ['unions'] }),
        ])
      }
      toaster.create({ title: 'Union affiliation added', type: 'success', duration: 3000 })
      onClose()
    },
    onError: () => {
      toaster.create({ title: 'Failed to add union affiliation', type: 'error', duration: 3000 })
    },
  })

  return (
    <Dialog.Root open={isOpen} size='md' onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>Add Union Affiliation</Dialog.Header>
            <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
            <Dialog.Body>
              <Field.Root required mb={4}>
                <Field.Label fontSize="sm">Union</Field.Label>
                <NativeSelect.Root size="sm">
                  <NativeSelect.Field
                    placeholder="Select a union"
                    value={unionId}
                    onChange={e => setUnionId(e.target.value)}>
                    {unionData?.items.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.fullName || u.name} {u.localNumber ? `(Local ${u.localNumber})` : ''}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>
              <HStack gap={4}>
                <Field.Root>
                  <Field.Label fontSize="sm">Contract Start</Field.Label>
                  <Input
                    size="sm"
                    type="date"
                    value={contractStartDate}
                    onChange={e => setContractStartDate(e.target.value)}
                  />
                </Field.Root>
                <Field.Root>
                  <Field.Label fontSize="sm">Contract End</Field.Label>
                  <Input
                    size="sm"
                    type="date"
                    value={contractEndDate}
                    onChange={e => setContractEndDate(e.target.value)}
                  />
                </Field.Root>
              </HStack>
              <Field.Root mt={4}>
                <Field.Label fontSize="sm">Union Rep</Field.Label>
                <Input
                  size="sm"
                  placeholder="Union rep name"
                  value={unionRepName}
                  onChange={e => setUnionRepName(e.target.value)}
                />
              </Field.Root>
              <Field.Root mt={4}>
                <Field.Label fontSize="sm">Employer Contact</Field.Label>
                <ContactSearchSelect
                  value={employerContactId || undefined}
                  displayName={employerContactName || undefined}
                  onSelect={(id, name) => {
                    setEmployerContactId(id)
                    setEmployerContactName(name)
                  }}
                  placeholder="Search for employer contact..."
                  allowFreeText
                />
              </Field.Root>

              {/* Worksite coverage toggles for business affiliations */}
              {isBusiness && (
                <>
                  <Separator my={4} />
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold" mb={1}>Worksite Coverage</Text>
                    <Text fontSize="xs" color="gray.500" mb={3}>
                      This union affiliation will propagate to the business's worksites. Toggle coverage per worksite:
                    </Text>
                    {worksitesLoading ? (
                      <VStack py={4}><Spinner size="sm" color="accent" /></VStack>
                    ) : !hasWorksites ? (
                      <Text fontSize="sm" color="gray.500" fontStyle="italic">No worksites under this business yet.</Text>
                    ) : (
                      <VStack gap={2} align="stretch">
                        {worksites.map(w => (
                          <HStack key={w.id} justify="space-between" px={3} py={2} bg="gray.50" borderRadius="md">
                            <Text fontSize="sm" fontWeight="medium" lineClamp={1} flex={1}>
                              {w.label}{w.city ? ` (${w.city})` : ''}
                            </Text>
                            <HStack gap={2}>
                              <Text fontSize="xs" color={excludedWorksiteIds[w.id] ? 'red.500' : 'green.600'} fontWeight="medium">
                                {excludedWorksiteIds[w.id] ? 'Excluded' : 'Covered'}
                              </Text>
                              <Switch.Root
                                size="sm"
                                checked={!excludedWorksiteIds[w.id]}
                                onCheckedChange={() => toggleWorksiteExclusion(w.id)}
                              >
                                <Switch.HiddenInput />
                                <Switch.Control>
                                  <Switch.Thumb />
                                </Switch.Control>
                              </Switch.Root>
                            </HStack>
                          </HStack>
                        ))}
                      </VStack>
                    )}
                  </Box>
                </>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
              <Button
                colorPalette="brand"
                onClick={() => mutation.mutate()}
                loading={mutation.isPending}
                disabled={!unionId}
              >
                Add Affiliation
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}
